<template>
    <div
        :class="[{ active: expanded }, 'dropdown-item']"
        :aria-haspopup="navItem.Options?.length ? 'true' : undefined"
        :aria-expanded="expanded"
        @mouseenter.stop="toggleMenu(navItem)"
        @mouseleave.stop="toggleMenu(navItem)"
        @keydown.stop="onKeydown"
        tabindex="0"
        role="button"
        ref="rootElement"
    >
        <component
            :is="!isMobileView && navItem.Link?.url ? 'a' : 'label'"
            :for="!isMobileView && navItem.Link?.url ? null : `submenu-toggle-${navItem._uid}`"
            :href="isMobileView ? null : navItem.Link?.url"
            data-cy="openSubMenu"
            :data-gtm-id="
                !isMobileView && navItem.Link?.url ? stringId('header_link', navItem.trackingId, navItem.Title) : null
            "
            aria-label="Open submenu"
            :aria-controls="`submenu-${navItem._uid}`"
            :class="['dropdown-item__label', `direction-${navItem.Direction}`]"
            @pointerdown="handleTouch($event, !!(!isMobileView && navItem.Link?.url))"
            @click="onClick"
        >
            {{ navItem.Title }}
            <NuxtIcon v-if="navItem.Options" name="ri:arrow-right-s-line" />
        </component>
        <input
            class="dropdown-item__submenu-checkbox hidden"
            type="checkbox"
            :id="`submenu-toggle-${navItem._uid}`"
            aria-hidden="true"
            data-cy="dropdownCheckbox"
        />
        <div
            v-if="navItem.component === 'Dropdown' && navItem.Options?.length"
            :class="[
                'dropdown-item__submenu',
                navItem.Direction,
                { 'active-submenu': hasActiveSubmenu },
                { [`expands-${submenuDirection || 'left'}`]: hasActiveSubmenu },
            ]"
            data-cy="dropdownSubmenu"
        >
            <label
                :for="`submenu-toggle-${navItem._uid}`"
                class="dropdown-item__submenu-back"
                data-cy="closeSubMenu"
                aria-label="Close submenu"
                :aria-controls="`submenu-${navItem._uid}`"
            >
                <NuxtIcon name="ri:arrow-left-s-line" />
                Back
            </label>
            <a
                v-if="navItem.Link.url"
                :href="navItem.Link.url"
                class="dropdown-item__title"
                data-cy="dropdownTitleLink"
                :data-gtm-id="stringId('header_link', navItem.trackingId, navItem.Title)"
            >
                {{ navItem.Title }}
            </a>
            <p v-else class="dropdown-item__title" data-cy="dropdownTitle">{{ navItem.Title }}</p>
            <ul
                :id="`submenu-${navItem._uid}`"
                class="dropdown-item__submenu-list"
                role="menu"
                data-cy="dropdownSubmenuList"
            >
                <li
                    v-for="(dropdownItem, dIndex) in navItem.Options"
                    class="dropdown-item__submenu-item"
                    :class="{
                        'current-page-nav-item': currentPageMap[dropdownItem._uid],
                    }"
                    :data-test="`menu-level-${menuLevel}-${dropdownItem._uid}`"
                    :key="`menu-level-${menuLevel}-${dropdownItem._uid}`"
                    role="menuitem"
                    :data-cy="`dropdownSubmenuItem-${dIndex}`"
                >
                    <DropdownItem
                        v-if="dropdownItem.component === 'Dropdown' && dropdownItem.Options?.length"
                        :data-cy="`dropdownSubmenuDropdownLevel${menuLevel}-${dIndex}`"
                        :nav-item="dropdownItem"
                        :menu-level="menuLevel + 1"
                        :opened-submenu-id="openedSubmenu"
                        @active-submenu="submenuHandle"
                        @close-parent="closeSubmenuHandle"
                    />
                    <a
                        v-else-if="
                            dropdownItem.component !== 'Dropdown' &&
                            (dropdownItem.Link?.url === '/logout' || dropdownItem.Link?.cached_url === '/logout')
                        "
                        :href="getInternalLink(dropdownItem.Link)"
                        :data-cy="`dropdownSubmenuLogoutLevel${menuLevel}-${dIndex}`"
                        :data-gtm-id="stringId('header_link', dropdownItem.trackingId, dropdownItem.Text)"
                        @click.prevent="logout()"
                    >
                        {{ dropdownItem.Text }}
                    </a>
                    <nuxt-link
                        v-else-if="dropdownItem.component !== 'Dropdown'"
                        :to="getInternalLink(dropdownItem.Link)"
                        :target="dropdownItem.Target"
                        :data-cy="`dropdownSubmenuLinkLevel${menuLevel}-${dIndex}`"
                        :data-gtm-id="stringId('header_link', dropdownItem.trackingId, dropdownItem.Text)"
                        external
                    >
                        {{ dropdownItem.Text }}
                    </nuxt-link>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup lang="ts">
import { getInternalLink } from '@nuxt-monorepo/base/app/utils/internalLinking';
import type { IMenuDropdown } from '@nuxt-monorepo/ui/types/store/global';
import resolveConfig from 'tailwindcss/resolveConfig';
import type { ILinkItem } from '@nuxt-monorepo/ui/types/components/CoreLinkList';
import { stringId } from '@nuxt-monorepo/ui/utils/helpers';
import DropdownItem from '@nuxt-monorepo/ui/components/Core/Navigation/DropdownItem.vue';

const route = useRoute();
const { navItem, menuLevel, openedSubmenuId } = defineProps<{
    navItem: IMenuDropdown;
    menuLevel: number;
    openedSubmenuId?: string | null;
}>();

function relativeLink(link: string) {
    if (!link) return;
    const fullUrl = new URL(link, useRequestURL().origin);
    return fullUrl.pathname;
}

const isCurrentPageNavItem = (navItem: IMenuDropdown | ILinkItem): boolean =>
    relativeLink(navItem?.Link?.url || navItem?.Link?.cached_url) === route.path;

// This Map required to make current page calculation static and avoid recalculation on any menu interaction
const currentPageMap = computed(() =>
    Object.fromEntries(navItem.Options.map((item) => [item._uid, isCurrentPageNavItem(item)]))
);

const emit = defineEmits(['activeSubmenu', 'closeParent', 'logout']);

const logout = () => {
    emit('logout');
};

const hasActiveSubmenu = ref(false);
const submenuDirection = ref('');
const openedSubmenu = ref<string | null>(null);

const rootElement = ref<HTMLDivElement | null>(null);

const expanded = ref(false);
const toggleMenu = (item: IMenuDropdown) => {
    if (!item.Options?.length) return;
    expanded.value = !expanded.value;
};

const submenuHandle = (state: { active: boolean; direction: string; openedSubmenu: string | null }) => {
    hasActiveSubmenu.value = state.active;
    submenuDirection.value = state.direction;
    openedSubmenu.value = state.openedSubmenu;
};
const closeSubmenu = () => {
    expanded.value = false;
    touchedOnce.value = false;
};

// Handles keyboard interactions
const onKeydown = (event: KeyboardEvent) => {
    switch (event.key) {
        case 'Enter':
        case ' ':
            if (navItem.Options?.length) {
                event.preventDefault();
                event.stopPropagation();

                expanded.value = !expanded.value;
            }
            break;
        case 'ArrowRight':
            if (navItem.Options?.length && !expanded.value) {
                event.preventDefault();
                event.stopPropagation();

                expanded.value = true;
            }
            break;
        case 'ArrowLeft':
        case 'Escape':
            if (navItem.Options?.length && expanded.value) {
                event.preventDefault();
                if (!(event.target as HTMLElement).classList.contains('dropdown-item')) {
                    event.stopPropagation();
                    const dropdownTitle: HTMLElement | null = (event.target as HTMLElement).closest('.dropdown-item');
                    dropdownTitle?.focus();
                }
                expanded.value = false;
            } else {
                emit('closeParent');
            }
            break;
    }
};

const closeSubmenuHandle = () => {
    closeSubmenu();
    rootElement.value?.focus();
};

watch(expanded, (val) => {
    emit('activeSubmenu', { active: val, direction: navItem.Direction, openedSubmenu: val ? navItem._uid : null });
});

watch(
    () => openedSubmenuId,
    (id) => {
        if (id && expanded.value && id !== navItem._uid) {
            closeSubmenu();
        }
    }
);

// There are different tailwind configs for different sites, need exact breakpoints values to avoid issues
const { tailwindCss } = useAppConfig();
const mobileBreakpoint = inject('mobileBreakpoint', 'lg');
// @ts-ignore tailwindCss.config type is different from real value
const fullConfig = resolveConfig(tailwindCss.config);
const mobileBreakpointPx = fullConfig.theme.screens[mobileBreakpoint];
const { isMobileView } = useIsMobileView(mobileBreakpointPx);

const touchedOnce = ref(false);
const handleTouch = (event: PointerEvent, isLink: boolean) => {
    if (event.pointerType !== 'touch') return;
    if (!isLink) return;
    event.preventDefault();
    if (!expanded.value) {
        touchedOnce.value = true;
    }
};
function onClick(e: PointerEvent) {
    if (e.pointerType === 'touch' && touchedOnce.value) {
        // prevent navigation on first tap
        e.preventDefault();
        touchedOnce.value = false;
        return;
    }
}
</script>

<style scoped lang="postcss">
.dropdown-item {
    @apply w-full;
    .md-mobile & {
        @apply md:relative md:h-full md:flex md:items-center;
    }
    .tablet-mobile & {
        @apply tablet:relative tablet:h-full tablet:flex tablet:items-center;
    }
    .lg-mobile & {
        @apply lg:relative lg:h-full lg:flex lg:items-center;
    }
    .xl-mobile & {
        @apply xl:relative xl:h-full xl:flex xl:items-center;
    }

    &.active,
    &:hover {
        .iconify {
            .md-mobile & {
                @apply md:-rotate-90;
            }
            .tablet-mobile & {
                @apply tablet:-rotate-90;
            }
            .lg-mobile & {
                @apply lg:-rotate-90;
            }
            .xl-mobile & {
                @apply xl:-rotate-90;
            }
        }

        & > .dropdown-item__submenu {
            .md-mobile & {
                @apply md:visible md:opacity-100;
            }
            .tablet-mobile & {
                @apply tablet:visible tablet:opacity-100;
            }
            .lg-mobile & {
                @apply lg:visible lg:opacity-100;
            }
            .xl-mobile & {
                @apply xl:visible xl:opacity-100;
            }
        }

        & > .dropdown-item__label {
            .md-mobile & {
                @apply md:text-primary;
            }
            .tablet-mobile & {
                @apply tablet:text-primary;
            }
            .lg-mobile & {
                @apply lg:text-primary;
            }
            .xl-mobile & {
                @apply xl:text-primary;
            }
        }
    }

    .iconify {
        .md-mobile & {
            @apply md:transform md:rotate-90 md:transition-transform ease-in-out duration-[400ms];
        }
        .tablet-mobile & {
            @apply tablet:transform tablet:rotate-90 tablet:transition-transform ease-in-out duration-[400ms];
        }
        .lg-mobile & {
            @apply lg:transform lg:rotate-90 lg:transition-transform ease-in-out duration-[400ms];
        }
        .xl-mobile & {
            @apply xl:transform xl:rotate-90 xl:transition-transform ease-in-out duration-[400ms];
        }
    }

    &__submenu {
        @apply fixed top-0 right-auto left-0 flex flex-col gap-4 transform translate-x-full transition-transform ease-in-out duration-[400ms] bottom-0 w-full bg-white z-50 h-[100dvh] px-4 pt-12 pb-4;
        box-shadow: 0 4px 4px rgba(0, 0, 0, 0.1);

        .md-mobile & {
            @apply md:transform-none md:absolute md:invisible md:h-auto md:transition-opacity md:opacity-0 md:top-full md:bottom-auto md:bg-white md:w-auto md:min-w-full md:py-3 md:px-0 md:rounded md:max-h-[80vh];
            /* Fix menu dropdown issue on desktop safari */
            @apply md:translate-x-0 md:transform-gpu md:will-change-transform md:isolate;
        }
        .tablet-mobile & {
            @apply tablet:transform-none tablet:absolute tablet:invisible tablet:h-auto tablet:transition-opacity tablet:opacity-0 tablet:top-full tablet:bottom-auto tablet:bg-white tablet:w-auto tablet:min-w-full tablet:py-3 tablet:px-0 tablet:rounded tablet:max-h-[80vh];
            /* Fix menu dropdown issue on desktop safari */
            @apply tablet:translate-x-0 tablet:transform-gpu tablet:will-change-transform tablet:isolate;
        }
        .lg-mobile & {
            @apply lg:transform-none lg:absolute lg:invisible lg:h-auto lg:transition-opacity lg:opacity-0 lg:top-full lg:bottom-auto lg:bg-white lg:w-auto lg:min-w-full lg:py-3 lg:px-0 lg:rounded lg:max-h-[80vh];
            /* Fix menu dropdown issue on desktop safari */
            @apply lg:translate-x-0 lg:transform-gpu lg:will-change-transform lg:isolate;
        }
        .xl-mobile & {
            @apply xl:transform-none xl:absolute xl:invisible xl:h-auto xl:transition-opacity xl:opacity-0 xl:top-full xl:bottom-auto xl:bg-white xl:w-auto xl:min-w-full xl:py-3 xl:px-0 xl:rounded xl:max-h-[80vh];
            /* Fix menu dropdown issue on desktop safari */
            @apply xl:translate-x-0 xl:transform-gpu xl:will-change-transform xl:isolate;
        }

        &.left {
            .md-mobile & {
                @apply md:left-0 md:right-auto;
            }
            .tablet-mobile & {
                @apply tablet:left-0 tablet:right-auto;
            }
            .lg-mobile & {
                @apply lg:left-0 lg:right-auto;
            }
            .xl-mobile & {
                @apply xl:left-0 xl:right-auto;
            }
        }

        &.right {
            .md-mobile & {
                @apply md:right-0 md:left-auto;
            }
            .tablet-mobile & {
                @apply tablet:right-0 tablet:left-auto;
            }
            .lg-mobile & {
                @apply lg:right-0 lg:left-auto;
            }
            .xl-mobile & {
                @apply xl:right-0 xl:left-auto;
            }
        }

        &.active-submenu {
            .md-mobile & {
                @apply md:rounded-br-none md:rounded-tr-none;
                ::-webkit-scrollbar {
                    @apply md:bg-white md:w-2 md:h-2;
                }
                ::-webkit-scrollbar-thumb {
                    @apply md:bg-gray-400 md:rounded-full;
                }
            }
            .tablet-mobile & {
                @apply tablet:rounded-br-none tablet:rounded-tr-none;
                ::-webkit-scrollbar {
                    @apply tablet:bg-white tablet:w-2 tablet:h-2;
                }
                ::-webkit-scrollbar-thumb {
                    @apply tablet:bg-gray-400 tablet:rounded-full;
                }
            }
            .lg-mobile & {
                @apply lg:rounded-br-none lg:rounded-tr-none;
                ::-webkit-scrollbar {
                    @apply lg:bg-white lg:w-2 lg:h-2;
                }
                ::-webkit-scrollbar-thumb {
                    @apply lg:bg-gray-400 lg:rounded-full;
                }
            }
            .xl-mobile & {
                @apply xl:rounded-br-none xl:rounded-tr-none;
                ::-webkit-scrollbar {
                    @apply xl:bg-white xl:w-2 xl:h-2;
                }
                ::-webkit-scrollbar-thumb {
                    @apply xl:bg-gray-400 xl:rounded-full;
                }
            }

            &.expands-right {
                .md-mobile & {
                    @apply md:rounded-bl-none md:rounded-tl-none md:rounded-br md:rounded-tr;
                }
                .tablet-mobile & {
                    @apply tablet:rounded-bl-none tablet:rounded-tl-none tablet:rounded-br tablet:rounded-tr;
                }
                .lg-mobile & {
                    @apply lg:rounded-bl-none lg:rounded-tl-none lg:rounded-br lg:rounded-tr;
                }
                .xl-mobile & {
                    @apply xl:rounded-bl-none xl:rounded-tl-none xl:rounded-br xl:rounded-tr;
                }
            }
        }
    }

    &__submenu-list {
        @apply max-h-full flex flex-col gap-3 overflow-y-auto overflow-x-hidden;
        .md-mobile & {
            @apply md:max-h-none md:overflow-x-visible md:gap-0;
        }
        .tablet-mobile & {
            @apply tablet:max-h-none tablet:overflow-x-visible tablet:gap-0;
        }
        .lg-mobile & {
            @apply lg:max-h-none lg:overflow-x-visible lg:gap-0;
        }
        .xl-mobile & {
            @apply xl:max-h-none xl:overflow-x-visible xl:gap-0;
        }
    }

    &__submenu-item {
        .md-mobile & {
            @apply md:flex md:text-nowrap;
        }
        .tablet-mobile & {
            @apply tablet:flex tablet:text-nowrap;
        }
        .lg-mobile & {
            @apply lg:flex lg:text-nowrap;
        }
        .xl-mobile & {
            @apply xl:flex xl:text-nowrap;
        }

        .dropdown-item {
            .md-mobile & {
                @apply md:static md:px-4 md:py-[6px];
                .dropdown-item__submenu {
                    @apply md:left-full md:top-0 md:rounded-tl-none md:rounded-bl-none md:bg-neutral-50;

                    &.right {
                        @apply md:rounded-tr-none md:rounded-br-none md:rounded-tl md:rounded-bl;
                    }

                    &:after {
                        @apply md:absolute md:top-0 md:left-0 md:h-full md:w-full md:content-[''] md:min-h-[60vh] -z-10;
                    }
                }
            }
            .tablet-mobile & {
                @apply tablet:static tablet:px-4 tablet:py-[6px];
                .dropdown-item__submenu {
                    @apply tablet:left-full tablet:top-0 tablet:rounded-tl-none tablet:rounded-bl-none tablet:bg-neutral-50;

                    &.right {
                        @apply tablet:rounded-tr-none tablet:rounded-br-none tablet:rounded-tl tablet:rounded-bl;
                    }

                    &:after {
                        @apply tablet:absolute tablet:top-0 tablet:left-0 tablet:h-full tablet:w-full tablet:content-[''] tablet:min-h-[60vh] -z-10;
                    }
                }
            }
            .lg-mobile & {
                @apply lg:static lg:px-4 lg:py-[6px];
                .dropdown-item__submenu {
                    @apply lg:left-full lg:top-0 lg:rounded-tl-none lg:rounded-bl-none lg:bg-neutral-50;

                    &.right {
                        @apply lg:rounded-tr-none lg:rounded-br-none lg:rounded-tl lg:rounded-bl;
                    }

                    &:after {
                        @apply lg:absolute lg:top-0 lg:left-0 lg:h-full lg:w-full lg:content-[''] lg:min-h-[60vh] -z-10;
                    }
                }
            }
            .xl-mobile & {
                @apply xl:static xl:px-4 xl:py-[6px];
                .dropdown-item__submenu {
                    @apply xl:left-full xl:top-0 xl:rounded-tl-none xl:rounded-bl-none xl:bg-neutral-50;

                    &.right {
                        @apply xl:rounded-tr-none xl:rounded-br-none xl:rounded-tl xl:rounded-bl;
                    }

                    &:after {
                        @apply xl:absolute xl:top-0 xl:left-0 xl:h-full xl:w-full xl:content-[''] xl:min-h-[60vh] -z-10;
                    }
                }
            }

            .dropdown-item__label .iconify {
                @apply transform-none;
            }

            .dropdown-item__submenu {
                &.right {
                    .md-mobile & {
                        @apply md:right-full md:left-auto;
                    }
                    .tablet-mobile & {
                        @apply tablet:right-full tablet:left-auto;
                    }
                    .lg-mobile & {
                        @apply lg:right-full lg:left-auto;
                    }
                    .xl-mobile & {
                        @apply xl:right-full xl:left-auto;
                    }
                }
            }

            .dropdown-item__label {
                &.direction-right {
                    .md-mobile & {
                        @apply md:flex-row-reverse;

                        .iconify {
                            @apply md:transform md:rotate-180;
                        }
                    }
                    .tablet-mobile & {
                        @apply tablet:flex-row-reverse;

                        .iconify {
                            @apply tablet:transform tablet:rotate-180;
                        }
                    }
                    .lg-mobile & {
                        @apply lg:flex-row-reverse;

                        .iconify {
                            @apply lg:transform lg:rotate-180;
                        }
                    }
                    .xl-mobile & {
                        @apply xl:flex-row-reverse;

                        .iconify {
                            @apply xl:transform xl:rotate-180;
                        }
                    }
                }
            }
        }

        & > a,
        & > button {
            .md-mobile & {
                @apply md:px-4 md:py-[6px] md:transition-colors;
            }
            .tablet-mobile & {
                @apply tablet:px-4 tablet:py-[6px] tablet:transition-colors;
            }
            .lg-mobile & {
                @apply lg:px-4 lg:py-[6px] lg:transition-colors;
            }
            .xl-mobile & {
                @apply xl:px-4 xl:py-[6px] xl:transition-colors;
            }

            &:focus-visible {
                outline: 2px solid currentColor;
                outline-offset: -2px;
            }
        }
    }

    &__submenu-checkbox:checked ~ &__submenu {
        @apply transform translate-x-0;
    }

    &__submenu-item > a {
        @apply flex-grow;
    }

    &__submenu-back,
    &__submenu-item > a {
        @apply flex items-center gap-2 font-medium text-sm min-h-6 text-left;

        .md-mobile & {
            @apply md:hover:underline md:text-base;
        }
        .tablet-mobile & {
            @apply tablet:hover:underline tablet:text-base;
        }
        .lg-mobile & {
            @apply lg:hover:underline lg:text-base;
        }
        .xl-mobile & {
            @apply xl:hover:underline xl:text-base;
        }
    }

    &__submenu-back {
        @apply px-1 py-1;

        .md-mobile & {
            @apply md:hidden;
        }
        .tablet-mobile & {
            @apply tablet:hidden;
        }
        .lg-mobile & {
            @apply lg:hidden;
        }
        .xl-mobile & {
            @apply xl:hidden;
        }

        .iconify {
            @apply w-4 h-4;
        }
    }

    &__label {
        @apply flex items-center justify-between gap-2 font-medium text-sm cursor-pointer;

        .md-mobile & {
            @apply md:w-full md:text-base md:transition-colors;
            .active > & {
                @apply md:text-primary;
            }
        }
        .tablet-mobile & {
            @apply tablet:w-full tablet:text-base tablet:transition-colors;
            .active > & {
                @apply tablet:text-primary;
            }
        }
        .lg-mobile & {
            @apply lg:w-full lg:text-base lg:transition-colors;
            .active > & {
                @apply lg:text-primary;
            }
        }
        .xl-mobile & {
            @apply xl:w-full xl:text-base xl:transition-colors;
            .active > & {
                @apply xl:text-primary;
            }
        }
    }

    &__title {
        @apply min-h-[48px] flex items-center text-sm font-semibold border-t border-t-[#D4D5DB] border-b border-b-[#D4D5DB];

        .md-mobile & {
            @apply md:hidden;
        }
        .tablet-mobile & {
            @apply tablet:hidden;
        }
        .lg-mobile & {
            @apply lg:hidden;
        }
        .xl-mobile & {
            @apply xl:hidden;
        }
    }
}
</style>
